import { useContext, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { AppContext } from '../context/app';
import type { Place } from '../context/types';
import { isSatellitePlace } from '../utils/typeGuards';
import { useGetNearestGeoNode } from './useGetNearestGeoNode';
import { useGetXYtoPOI } from './useGetXYtoPOI';
import { useKioskManager } from './useKioskManager';
import { useLocationParams } from './useLocationParams';

export const useNavigationManager = () => {
  const { kioskRegistration } = useKioskManager();
  const {
    navDestinationPlace,
    setNavDestinationPlace,
    setNavStartingPlace,
    navStartingPlace,
    setShowPrintDirectionView,
    setSelectedSite,
    selectedSite,
    availableSites,
  } = useContext(AppContext);

  const navStartingPlaceIsOnCampus = useMemo(
    () =>
      Boolean(
        (navStartingPlace &&
          'location' in navStartingPlace &&
          (navStartingPlace as Place)) ||
          kioskRegistration,
      ),
    [kioskRegistration, navStartingPlace],
  );

  // Nearest Geo Node is needed when navigation is started from a place that is not on campus
  const { data: arrivalGeoNode, isLoading: geoNodeIsLoading } =
    useGetNearestGeoNode(
      navStartingPlaceIsOnCampus
        ? undefined
        : (navDestinationPlace as any)?.location,
    );
  const { data: startingGeoNode, isLoading: startingGeoNodeIsLoading } =
    useGetNearestGeoNode((navStartingPlace as any)?.location);

  const startingPoint = useMemo(() => {
    if (kioskRegistration) {
      return {
        srcBuilding: kioskRegistration.building_id,
        srcFloor: kioskRegistration.floor_id,
        srcX: kioskRegistration.x && kioskRegistration.x + 0.01,
        srcY: kioskRegistration.y && kioskRegistration.y + 0.01,
        key: kioskRegistration.kiosk_id,
      };
    }
    if (
      navStartingPlace &&
      'location' in navStartingPlace &&
      (navStartingPlace as Place)
    ) {
      return {
        srcBuilding: navStartingPlace?.location.building,
        srcFloor: navStartingPlace?.location.floor,
        srcX: navStartingPlace.x,
        srcY: navStartingPlace.y,
        key: navStartingPlace?.location.id,
      };
    }
    if (arrivalGeoNode) {
      return {
        srcBuilding: arrivalGeoNode.data.payload.geoInfo.building,
        srcFloor: arrivalGeoNode.data.payload.geoInfo.floor,
        srcX: arrivalGeoNode.data.payload.geoInfo.x,
        srcY: arrivalGeoNode.data.payload.geoInfo.y,
        key: arrivalGeoNode.data.payload.geoInfo.key,
      };
    }
    return {};
  }, [arrivalGeoNode, kioskRegistration, navStartingPlace]);
  const { data: legTwoRoute, isLoading: isGetLegTwoRouteLoading } =
    useGetXYtoPOI(
      startingPoint?.srcBuilding,
      startingPoint?.srcFloor,
      startingPoint?.srcX,
      startingPoint?.srcY,
      startingPoint?.key,
      navDestinationPlace ? (navDestinationPlace as any)?.location : undefined,
    );

  const locationParams = useLocationParams();

  useEffect(() => {
    if (locationParams.navDestinationPlace) {
      setNavDestinationPlace(locationParams.navDestinationPlace);
      const destinationSiteId = isSatellitePlace(
        locationParams.navDestinationPlace,
      )
        ? selectedSite
        : locationParams.navDestinationPlace.location.site;

      const destinationSite = availableSites?.find(
        (site) => site.id === destinationSiteId,
      );

      setSelectedSite(destinationSite);
    } else {
      setNavDestinationPlace(undefined);
    }
    if (locationParams.navStartingPlace) {
      setNavStartingPlace(locationParams.navStartingPlace);
    } else {
      setNavStartingPlace(undefined);
    }
  }, [
    availableSites,
    locationParams,
    selectedSite,
    setNavDestinationPlace,
    setNavStartingPlace,
    setSelectedSite,
  ]);

  const { search } = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(search);
    const isPrintMode = params.get('printMode');

    if (isPrintMode) {
      setShowPrintDirectionView(isPrintMode.toLowerCase() === 'true');
    }
  }, [search, setShowPrintDirectionView]);

  return {
    arrivalGeoNode: arrivalGeoNode?.data.payload.geoInfo,
    geoNodeIsLoading,
    legTwoRoute: legTwoRoute?.data.payload,
    isGetLegTwoRouteLoading,
    startingGeoNode,
    startingGeoNodeIsLoading,
  };
};
